import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { isValidNumber } from '../../../../../../../utils/math/number';
import { isDefined } from '../../../../../../../utils/variables';
import { ActionName, DesignTool } from '../../../../../../../viewer/tools/design-tool';
import { interpolatePointHeightsUtils } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/commands/features/interpolate-point-heights-utils';
import { applyActionInProgress } from '../../../../../../../viewer/tools/utils/context-menu/design-tool/commands/general';
import { useButtonHotkey } from '../../../../../../hooks/use-button-hotkey';
import { Button } from '../../../../../button/button';
import { InfoBox } from '../../../../../info-box/info-box';
import { LabelledContainer } from '../../../../../labelled-container/labelled-container';
import { Select } from '../../../../../select/select';
import { Stack } from '../../../../../stack/stack';
import { TextBox } from '../../../../../text-box/text-box';
import { useActionInProgress } from '../hooks/use-action-in-progress';

type Props = {
  tool: DesignTool;
};

export type PointHeightInterpolationMode = 'slope' | 'interpolateStartEnd' | 'interpolateKeyPoints';

const InterpolatePointHeights = (props: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [mode, setMode] = useState<PointHeightInterpolationMode>(
    props.tool.pointHeightInterpolationMode,
  );
  const [slopeInput, setSlopeInput] = useState(props.tool.pointHeightInterpolationSlope.toString());
  const { actionInProgress, isActionInProgress, isReadyToApply, originPointId } =
    useActionInProgress(props.tool, ActionName.InterpolatePointHeights);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  useButtonHotkey(confirmButtonRef, 'Enter');

  const hasStartPoint = isActionInProgress && isDefined(originPointId);
  const readyToApply = hasStartPoint && isReadyToApply;
  const invalidInput = mode === 'slope' ? !isValidNumber(Number.parseFloat(slopeInput)) : false;

  const options = useRef<{ id: PointHeightInterpolationMode; name: string }[]>([
    {
      id: 'interpolateStartEnd',
      name: t('section.functions.interpolatePointHeights.options.interpolateStartEnd', {
        ns: 'skyviewDesign',
      }),
    },
    {
      id: 'interpolateKeyPoints',
      name: t('section.functions.interpolatePointHeights.options.interpolateAll', {
        ns: 'skyviewDesign',
      }),
    },
    {
      id: 'slope',
      name: t('section.functions.interpolatePointHeights.options.slope', { ns: 'skyviewDesign' }),
    },
  ]);

  return (
    <Stack spacing={1}>
      {!isActionInProgress && (
        <InfoBox
          color="yellow"
          leftIcon={{ color: theme.color.blue, fixedWidth: true, icon: ['fas', 'circle-info'] }}
        >
          {t('section.functions.interpolatePointHeights.content.infoStart', {
            ns: 'skyviewDesign',
          })}
        </InfoBox>
      )}

      {isActionInProgress && !hasStartPoint && (
        <InfoBox
          color="yellow"
          leftIcon={{ color: theme.color.blue, fixedWidth: true, icon: ['fas', 'circle-info'] }}
        >
          {t('section.functions.interpolatePointHeights.content.infoSelectStartPoint', {
            ns: 'skyviewDesign',
          })}
        </InfoBox>
      )}

      {hasStartPoint && !readyToApply && (
        <InfoBox
          color="yellow"
          leftIcon={{ color: theme.color.blue, fixedWidth: true, icon: ['fas', 'circle-info'] }}
        >
          {t('section.functions.interpolatePointHeights.content.infoSelectNextPoint', {
            ns: 'skyviewDesign',
          })}
        </InfoBox>
      )}

      {readyToApply && (
        <InfoBox
          color="yellow"
          leftIcon={{ color: theme.color.blue, fixedWidth: true, icon: ['fas', 'circle-info'] }}
        >
          {t('section.functions.interpolatePointHeights.content.infoSelectNextPointOrConfirm', {
            ns: 'skyviewDesign',
          })}
        </InfoBox>
      )}

      <LabelledContainer
        text={t('section.functions.interpolatePointHeights.content.method', {
          ns: 'skyviewDesign',
        })}
      >
        <Select
          options={options.current}
          value={mode}
          onChange={(e) => {
            const newMode = e.target.value as PointHeightInterpolationMode;
            setMode(newMode);
            props.tool.pointHeightInterpolationMode = newMode;
          }}
        />
      </LabelledContainer>

      {mode === 'slope' && (
        <LabelledContainer
          text={t('section.snap.relativeSlopePercentButton.text', { ns: 'skyviewDesign' })}
        >
          <TextBox
            errorMessage={
              invalidInput
                ? t('section.snap.validation.invalidNumber', { ns: 'skyviewDesign' })
                : undefined
            }
            max={Number.MAX_SAFE_INTEGER}
            min={Number.MIN_SAFE_INTEGER}
            type="number"
            value={slopeInput}
            onChange={(e) => {
              setSlopeInput(e.target.value);
              const value = Number.parseFloat(e.target.value);
              if (isValidNumber(value)) {
                props.tool.pointHeightInterpolationSlope = value;
              }
            }}
          />
        </LabelledContainer>
      )}

      {actionInProgress ? (
        <Button
          disabled={!isReadyToApply || invalidInput}
          ref={confirmButtonRef}
          variant="contained"
          onClick={() => applyActionInProgress(props.tool).action()}
        >
          {t('section.functions.interpolatePointHeights.content.buttonConfirm', {
            ns: 'skyviewDesign',
          })}
        </Button>
      ) : (
        <Button
          ref={confirmButtonRef}
          variant="contained"
          onClick={() => interpolatePointHeightsUtils.initInterpolatePointHeightsAction(props.tool)}
        >
          {t('section.functions.interpolatePointHeights.content.buttonStart', {
            ns: 'skyviewDesign',
          })}
        </Button>
      )}
    </Stack>
  );
};

export { InterpolatePointHeights };
